import React, { useState } from 'react';
import './AppleDiscoveryModes.css';
import { EnrichedGameCard } from './SharedGameEnrichment';

const AppleDiscoveryModes = ({ discoveryModes, playerName, season }) => {
    const [selectedMode, setSelectedMode] = useState(null);

    if (!discoveryModes || Object.keys(discoveryModes).length === 0) {
        return (
            <div className="apple-discovery-empty">
                <div className="empty-state">
                    <div className="empty-icon">🎯</div>
                    <h3>No Discovery Modes</h3>
                    <p>No special game patterns found for {playerName} in {season}.</p>
                </div>
            </div>
        );
    }

    const modeEntries = Object.entries(discoveryModes);

    return (
        <div className="apple-discovery-modes">
            <div className="discovery-header">
                <h2>🎯 Discovery Modes</h2>
                <p>Explore {playerName}'s {season} season through different lenses</p>
            </div>

            {!selectedMode ? (
                <div className="modes-grid">
                    {modeEntries.map(([key, mode]) => (
                        <ModeCard
                            key={key}
                            modeKey={key}
                            mode={mode}
                            onClick={() => setSelectedMode({ key, mode })}
                        />
                    ))}
                </div>
            ) : (
                <div className="mode-detail">
                    <div className="mode-detail-header">
                        <button 
                            className="back-button"
                            onClick={() => setSelectedMode(null)}
                        >
                            ← Back to Discovery Modes
                        </button>
                        <div className="mode-title">
                            <h3>{selectedMode.mode.name}</h3>
                            <p>{selectedMode.mode.description}</p>
                        </div>
                    </div>
                    
                    <div className="mode-games">
                        <div className="games-count">
                            {selectedMode.mode.games?.length || 0} games found
                        </div>
                        <div className="games-list">
                            {(selectedMode.mode.games || []).map((game, idx) => (
                                <div key={idx} className="discovery-game-card">
                                    <EnrichedGameCard game={game} />
                                    {game.discoveryContext && (
                                        <div className="discovery-context">
                                            <div className="context-reason">
                                                {game.discoveryContext.reason}
                                            </div>
                                            {game.discoveryContext.highlights && (
                                                <div className="context-highlights">
                                                    {game.discoveryContext.highlights.map((highlight, hIdx) => (
                                                        <span key={hIdx} className="highlight-tag">
                                                            {highlight}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const ModeCard = ({ modeKey, mode, onClick }) => {
    const getModeIcon = (key) => {
        const icons = {
            'revenge_tour': '🔥',
            'clutch_theater': '⏰',
            'giant_slayer': '⚔️',
            'statement_games': '💪',
            'efficiency_clinic': '🎯',
            'defensive_showcase': '🛡️',
            'comeback_artist': '📈',
            'playoff_preview': '🏆'
        };
        return icons[key] || '⭐';
    };

    const getModeGradient = (key) => {
        const gradients = {
            'revenge_tour': 'linear-gradient(135deg, #ff6b6b, #ee5a24)',
            'clutch_theater': 'linear-gradient(135deg, #feca57, #ff9ff3)',
            'giant_slayer': 'linear-gradient(135deg, #48dbfb, #0abde3)',
            'statement_games': 'linear-gradient(135deg, #ff9ff3, #f368e0)',
            'efficiency_clinic': 'linear-gradient(135deg, #1dd1a1, #10ac84)',
            'defensive_showcase': 'linear-gradient(135deg, #54a0ff, #2e86de)',
            'comeback_artist': 'linear-gradient(135deg, #5f27cd, #341f97)',
            'playoff_preview': 'linear-gradient(135deg, #ff6348, #e17055)'
        };
        return gradients[key] || 'linear-gradient(135deg, #a55eea, #8854d0)';
    };

    return (
        <div 
            className="apple-mode-card" 
            onClick={onClick}
            style={{ background: getModeGradient(modeKey) }}
        >
            <div className="mode-card-content">
                <div className="mode-icon">{getModeIcon(modeKey)}</div>
                <div className="mode-info">
                    <h4 className="mode-name">{mode.name}</h4>
                    <p className="mode-description">{mode.description}</p>
                    <div className="mode-stats">
                        <span className="games-count">
                            {mode.games?.length || 0} games
                        </span>
                        {mode.avg_score && (
                            <span className="avg-score">
                                Avg: {mode.avg_score.toFixed(1)}
                            </span>
                        )}
                    </div>
                </div>
            </div>
            <div className="mode-card-arrow">→</div>
        </div>
    );
};

export default AppleDiscoveryModes;
